import {IconButton} from '@dropbox/dig-components/buttons';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronDownLine, ChevronRightLine} from '@dropbox/dig-icons/assets';
import {type ReactNode, useEffect, useMemo, useState} from 'react';

import {DSYS_RAIL_ROW_INSET_PX, DSYS_TOUCH_TARGET_PX} from './layoutConstants';
import {useExclusiveHover} from './useExclusiveHover';
import {useHoverShade} from './useHoverShade';

type Bleed = {left: number; right: number};
type HoverState = ReturnType<typeof useExclusiveHover>;

const rowFlex: React.CSSProperties = {display: 'flex', alignItems: 'center', gap: 8, width: '100%'};

const isPlainLeftClick = (event: React.MouseEvent) =>
  !event.defaultPrevented &&
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;

/**
 * One rail row: an optional leading mark, the label (truncated), trailing
 * controls, then the chevron in a trailing column. Rows with an `href` are
 * anchors, so cmd / middle click open it in a new tab while a plain click calls
 * `onActivate`; rows without one are buttons. Full-bleed rails pass `bleed` (the
 * rail's padding, which the row's container cancels with negative margins), so
 * fills run edge to edge; without it rows are rounded.
 */
export const RailTreeRow = ({
  label,
  variant = 'item',
  tone = 'subtle',
  indentPx = 0,
  selected = false,
  selectedStyle = 'fill',
  leading,
  leadingGap = 8,
  labelAccessory,
  trailing,
  expandable = false,
  expanded = false,
  onToggle,
  reserveChevron = false,
  href,
  onActivate,
  bleed,
  touch = false,
  ariaPressed,
  hover,
  truncateLocation,
  subtleLabel = false,
}: {
  label: string;
  // 'group': a section header (bold, subtle until selected, 36px); 'item': 32px.
  variant?: 'item' | 'group';
  // A group's text until selected: 'subtle' (Workstreams' pillars) or 'standard'.
  tone?: 'subtle' | 'standard';
  // Extra left indent (depth); the text otherwise starts on the rail's row inset.
  indentPx?: number;
  selected?: boolean;
  // 'bold' marks a selected row with bold text only, no fill.
  selectedStyle?: 'fill' | 'bold';
  leading?: ReactNode;
  leadingGap?: number;
  // After the label (e.g. a Toggletip); its clicks don't reach the row.
  labelAccessory?: ReactNode;
  // Before the chevron (a pin, a link, a badge).
  trailing?: ReactNode;
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  // An invisible chevron slot, so trailing icons line up with expandable rows'.
  reserveChevron?: boolean;
  href?: string;
  onActivate: () => void;
  bleed?: Bleed;
  // Narrow screens: 44px rows and chevron targets.
  touch?: boolean;
  ariaPressed?: boolean;
  // The row's hover, when the caller needs it too (e.g. to show a pin on hover).
  hover?: HoverState;
  // Where a long label is cut: 1 (end, the default) or 0.5 (middle, for paths).
  truncateLocation?: number;
  // A secondary row (e.g. a search match's reference): gray until selected.
  subtleLabel?: boolean;
}) => {
  const ownHover = useExclusiveHover();
  const {hovered, onMouseEnter, onMouseLeave} = hover ?? ownHover;
  const isGroup = variant === 'group';
  const showSelectedFill = selected && selectedStyle === 'fill';
  // A selected row keeps its own fill; the hover shade would darken it.
  const shade = useHoverShade(hovered && !showSelectedFill);

  const className = `${atoms({
    display: 'flex',
    alignItems: 'center',
    borderRadius: bleed ? undefined : 'Medium',
    backgroundColor: showSelectedFill ? 'Opacity Surface - State 2' : undefined,
    paddingY: '4',
    paddingRight: '8',
    cursor: 'pointer',
  })} ${shade.className}`;
  const style: React.CSSProperties = {
    ...shade.style,
    paddingLeft: DSYS_RAIL_ROW_INSET_PX + indentPx + (bleed?.left ?? 0),
    paddingRight: 8 + (bleed?.right ?? 0),
    minHeight: touch ? DSYS_TOUCH_TARGET_PX : isGroup ? 36 : 32,
  };

  const chevron =
    expandable || reserveChevron ? (
      // Touch: a 44px target, pulled into the row's 4px padding so rows stay 44px.
      <Box
        aria-hidden={(!expandable && reserveChevron) || undefined}
        style={{
          flexShrink: 0,
          marginTop: touch ? -4 : 0,
          marginBottom: touch ? -4 : 0,
          visibility: !expandable && reserveChevron ? 'hidden' : undefined,
        }}
      >
        <IconButton
          variant="transparent"
          size="small"
          tabIndex={!expandable && reserveChevron ? -1 : undefined}
          style={touch ? {width: DSYS_TOUCH_TARGET_PX, height: DSYS_TOUCH_TARGET_PX} : undefined}
          onClick={(event: React.SyntheticEvent) => {
            event.preventDefault();
            event.stopPropagation();
            onToggle?.();
          }}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          <UIIcon src={expanded ? ChevronDownLine : ChevronRightLine} size="small" />
        </IconButton>
      </Box>
    ) : null;

  const content = (
    <div style={rowFlex}>
      <div
        style={{flexGrow: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: leadingGap}}
      >
        {leading}
        <div style={{minWidth: 0}}>
          {/* Groups read as sections: always bold, subtle until selected; items
              bold only when selected. */}
          <Text
            size="small"
            isBold={selected || isGroup}
            color={
              !selected && ((isGroup && tone === 'subtle') || subtleLabel) ? 'subtle' : 'standard'
            }
          >
            {/* No tooltip on hover: long names just end in an ellipsis. */}
            <Truncate
              lines={1}
              location={truncateLocation}
              observeParent={truncateLocation !== undefined}
            >
              {label}
            </Truncate>
          </Text>
        </div>
        {labelAccessory && (
          <Box onClick={(event: React.MouseEvent) => event.stopPropagation()}>{labelAccessory}</Box>
        )}
      </div>
      {/* Trailing controls sit flush together, each in a 24px slot, so they line
          up down the rail. */}
      {(trailing || chevron) && (
        <Box display="flex" alignItems="center" style={{flexShrink: 0}}>
          {trailing}
          {chevron}
        </Box>
      )}
    </div>
  );

  if (href) {
    return (
      <a
        href={href}
        // Mouse clicks don't take focus, so the focus ring only shows for keyboard use.
        onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
          if (!isPlainLeftClick(event)) return;
          event.preventDefault();
          onActivate();
        }}
        className={className}
        style={{...style, textDecoration: 'none', color: 'inherit'}}
      >
        {content}
      </a>
    );
  }

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={ariaPressed}
      onClick={onActivate}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onActivate();
        }
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
      className={className}
      style={style}
    >
      {content}
    </Box>
  );
};

export type RailTreeNode<T> = {
  id: string | number;
  label: string;
  // The row's URL (cmd-click / new tab); a plain click calls onSelect.
  href?: string;
  children: RailTreeNode<T>[];
  data: T;
};

// Ids from the roots down to the node with `id`, or [].
const findRailPath = <T,>(
  nodes: RailTreeNode<T>[],
  id: string | number
): Array<string | number> => {
  for (const node of nodes) {
    if (node.id === id) return [node.id];
    const path = findRailPath(node.children, id);
    if (path.length) return [node.id, ...path];
  }
  return [];
};

type RailTreeShared<T> = {
  selectedId?: string | number;
  onSelect: (node: RailTreeNode<T>) => void;
  indentPerLevel: number;
  renderLeading?: (node: RailTreeNode<T>, state: {selected: boolean}) => ReactNode;
  renderTrailing?: (
    node: RailTreeNode<T>,
    state: {selected: boolean; hovered: boolean}
  ) => ReactNode;
  bleed?: Bleed;
  touch?: boolean;
  groupRoots: boolean;
  groupTone: 'subtle' | 'standard';
  tintSelectedBranch: boolean;
};

const RailTreeNodeView = <T,>({
  node,
  depth,
  onPath,
  revealKey,
  path,
  shared,
}: {
  node: RailTreeNode<T>;
  depth: number;
  // On the way to the selection: opens on mount and when the selection changes.
  onPath: boolean;
  revealKey: string | null;
  path: Array<string | number>;
  shared: RailTreeShared<T>;
}) => {
  const hasChildren = node.children.length > 0;
  // Open at mount (not in an effect), so a refresh doesn't paint it folded first.
  const [expanded, setExpanded] = useState(onPath);
  useEffect(() => {
    if (onPath) setExpanded(true);
    // Only a new selection opens it; the user can fold it again after.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealKey]);
  const hover = useExclusiveHover();
  const selected = shared.selectedId === node.id;
  const isGroup = depth === 0 && shared.groupRoots;

  return (
    <Box>
      <RailTreeRow
        label={node.label}
        variant={isGroup ? 'group' : 'item'}
        tone={shared.groupTone}
        indentPx={depth * shared.indentPerLevel}
        selected={selected}
        // On a tinted branch a selected subtle header is marked by turning bold and
        // dark, not filled, so the fill doesn't stack on the tint. Standard headers
        // are already dark and bold, so they keep the fill.
        selectedStyle={
          isGroup && shared.tintSelectedBranch && shared.groupTone === 'subtle' ? 'bold' : 'fill'
        }
        leading={shared.renderLeading?.(node, {selected})}
        trailing={shared.renderTrailing?.(node, {selected, hovered: hover.hovered})}
        expandable={hasChildren}
        expanded={expanded}
        onToggle={() => setExpanded((value) => !value)}
        href={node.href}
        onActivate={() => {
          shared.onSelect(node);
          // Selecting a row also reveals its children (folding is chevron-only).
          if (hasChildren) setExpanded(true);
        }}
        bleed={shared.bleed}
        touch={shared.touch}
        hover={hover}
      />
      {expanded &&
        node.children.map((child) => (
          <RailTreeNodeView
            key={child.id}
            node={child}
            depth={depth + 1}
            onPath={path.includes(child.id)}
            revealKey={revealKey}
            path={path}
            shared={shared}
          />
        ))}
    </Box>
  );
};

/**
 * A plain hierarchy in a rail (Teams' tree), drawn with `RailTreeRow`: rows
 * indent per level, the chevron folds, and a click selects (and opens) a row.
 * The path to `selectedId` opens on mount and whenever the selection changes.
 * With `bleed`, the tree cancels the rail's padding so fills run edge to edge.
 * `groupRoots` draws the top level as section headers and `tintSelectedBranch`
 * tints the top-level branch holding the selection, like Workstreams' pillars.
 */
export const RailTree = <T,>({
  nodes,
  selectedId,
  onSelect,
  indentPerLevel = 20,
  renderLeading,
  renderTrailing,
  bleed,
  touch,
  groupRoots = false,
  groupTone = 'subtle',
  tintSelectedBranch = false,
}: {
  nodes: RailTreeNode<T>[];
  selectedId?: string | number;
  onSelect: (node: RailTreeNode<T>) => void;
  indentPerLevel?: number;
  renderLeading?: RailTreeShared<T>['renderLeading'];
  renderTrailing?: RailTreeShared<T>['renderTrailing'];
  bleed?: Bleed;
  touch?: boolean;
  // Top-level rows as section headers (bold, subtle until selected).
  groupRoots?: boolean;
  // The headers' text until selected: 'subtle' (gray) or 'standard'.
  groupTone?: 'subtle' | 'standard';
  // A tint over the top-level branch (with children) that holds the selection.
  tintSelectedBranch?: boolean;
}) => {
  const path = useMemo(
    () => (selectedId === undefined ? [] : findRailPath(nodes, selectedId)),
    [nodes, selectedId]
  );
  const revealKey = selectedId === undefined ? null : String(selectedId);
  const shared: RailTreeShared<T> = {
    selectedId,
    onSelect,
    indentPerLevel,
    renderLeading,
    renderTrailing,
    bleed,
    touch,
    groupRoots,
    groupTone,
    tintSelectedBranch,
  };

  return (
    <Box style={bleed ? {marginLeft: -bleed.left, marginRight: -bleed.right} : undefined}>
      {nodes.map((node) => (
        <Box
          key={node.id}
          borderRadius={bleed ? undefined : 'Medium'}
          backgroundColor={
            tintSelectedBranch && node.children.length > 0 && path[0] === node.id
              ? 'Opacity Surface'
              : undefined
          }
        >
          <RailTreeNodeView
            node={node}
            depth={0}
            onPath={path.includes(node.id)}
            revealKey={revealKey}
            path={path}
            shared={shared}
          />
        </Box>
      ))}
    </Box>
  );
};
